import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentService } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';

/**
 * Página que lista pagamentos aguardando confirmação do recebedor.
 */
function PendingConfirmationPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getAwaitingConfirmation();
      setPayments(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const handleConfirmPayment = async (paymentId) => {
    try {
      await paymentService.markManualReceived(paymentId);
      alert('✓ Recebimento confirmado com sucesso!');
      loadPayments(); // Recarrega a lista
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao confirmar recebimento');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">
          <i className="fa-solid fa-bell page-title-icon"></i>
          Pagamentos para Confirmar
        </h1>
        <p className="page-subtitle">
          Pagamentos que você precisa confirmar o recebimento
        </p>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 24 }}>
          {error}
        </div>
      )}

      {payments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <i className="fa-duotone fa-check-circle"></i>
          </div>
          <h3 className="empty-state-title">Tudo em dia!</h3>
          <p className="empty-state-text">
            Você não tem pagamentos aguardando confirmação.
          </p>
          <button 
            onClick={() => navigate('/groups')}
            className="btn btn-primary"
          >
            Voltar aos Grupos
          </button>
        </div>
      ) : (
        <div className="grid-layout">
          {payments.map((payment) => (
            <div 
              key={payment.id} 
              className="card-modern animate-fadeIn"
              style={{ padding: 24 }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 16,
                gap: 16
              }}>
                <div>
                  <div style={{ 
                    fontSize: 12, 
                    color: 'var(--text-muted)',
                    marginBottom: 4
                  }}>
                    {payment.groupName}
                  </div>
                  <div style={{ 
                    fontSize: 24, 
                    fontWeight: 700,
                    color: 'var(--primary-color)'
                  }}>
                    {formatCurrency(payment.amount)}
                  </div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  animation: 'pulse 2s infinite'
                }}>
                  <i className="fa-solid fa-bell"></i>
                  Aguardando
                </div>
              </div>

              <div style={{ 
                padding: 16,
                background: 'var(--bg-secondary)',
                borderRadius: 12,
                marginBottom: 16
              }}>
                <div style={{ 
                  display: 'grid',
                  gap: 12,
                  fontSize: 14
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>De:</span>
                    <span style={{ fontWeight: 600 }}>{payment.payerName}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Marcado como pago:</span>
                    <span style={{ fontWeight: 500 }}>{formatDate(payment.manualPaidAt)}</span>
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex',
                gap: 8
              }}>
                <button
                  className="btn btn-success"
                  style={{ flex: 1 }}
                  onClick={() => handleConfirmPayment(payment.id)}
                >
                  <i className="fa-solid fa-check-double"></i>
                  Confirmar Recebimento
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/payments/${payment.id}`)}
                >
                  <i className="fa-solid fa-eye"></i>
                  Ver
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PendingConfirmationPage;
